import React, {
  ReactElement,
  ReactNode,
  ReactNodeArray,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { ActionIcon, Portal, Stack, Tooltip } from '@mantine/core'
import { noop } from 'lodash'
import { Coordinate } from 'ol/coordinate'
import { toLonLat } from 'ol/proj'

import Checkbox from '../../../components/Checkbox'
import Field from '../../../components/Field'
import Form, { OnChange } from '../../../components/Form'
import Icon from '../../../components/Icon'
import { useMap } from '../useMap'
import { MapStyleSelect } from './MapStyleSelect'
import { useMapSettings } from './useMapSettings'
import Slidebar from '../../../components/Slidebar'

type MapSettingsProps = {
  extraSettingsForms?: ReactNode | ReactNodeArray
}

export const MapSettings = (props: MapSettingsProps): ReactElement => {
  const { extraSettingsForms } = props

  const { settings, setSettings } = useMapSettings()

  const [isOpen, setIsOpen] = useState(false)

  const { refMap, map } = useMap()

  const onChange = ({ values }): void => {
    setSettings(values)
  }

  const onMoveEnd = useCallback(() => {
    const view = map.getView()
    const zoom = view.getZoom()
    const [lon, lat] = toLonLat(view.getCenter() as Coordinate)
    setSettings({
      center: [lon, lat],
      zoom,
    })
  }, [map])

  useEffect(() => {
    map.on('moveend', onMoveEnd)

    return () => {
      map.un('moveend', onMoveEnd)
    }
  }, [onMoveEnd])

  if (!refMap) return <></>

  return (
    <>
      <div>
        <Tooltip
          label={
            isOpen
              ? i18n.t('close_map_settings', 'Karteneinstellungen schliessen')
              : i18n.t('open_map_settings', 'Karteneinstellungen öffnen')
          }
          withinPortal
        >
          <ActionIcon
            variant={isOpen ? 'filled' : 'light'}
            size={40}
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon icon="fas fa-cog" />
          </ActionIcon>
        </Tooltip>
      </div>
      <Portal target={refMap}>
        <Slidebar
          title={i18n.t('map_settings', 'Karteneinstellungen')}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          top={76}
        >
          <div>
            <div className="px-2">
              <Stack>
                <Form
                  onSubmit={noop}
                  initialValues={settings}
                >
                  <OnChange
                    preventFireOnMount
                    onChange={onChange}
                  />
                  <Stack spacing="xs">
                    <div>
                      <Field
                        name="tileLayer"
                        label={i18n.t('map_layer', 'Kartenansicht')}
                      >
                        <MapStyleSelect />
                      </Field>
                    </div>
                    {/(localhost|local)/.test(window.location.hostname) && (
                      <div>
                        <Field name="isVector">
                          <Checkbox>
                            {i18n.t(
                              'use_vector_tiles',
                              'Vektortiles verwenden',
                            )}
                          </Checkbox>
                        </Field>
                      </div>
                    )}
                  </Stack>
                </Form>
                {extraSettingsForms}
              </Stack>
            </div>
          </div>
        </Slidebar>
      </Portal>
    </>
  )
}
